import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface AuthContextValue {
  user: any;
  organization: any;
  permissions: string[];
  token: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<{ success: boolean }>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissionList: string[]) => boolean;
  hasAllPermissions: (permissionList: string[]) => boolean;
  isSuperAdmin: () => boolean;
  isTenantAdmin: () => boolean;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [token]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Call logout endpoint — axios header is already set via the token useEffect
      const currentToken = localStorage.getItem('authToken');
      if (currentToken) {
        await axios.post(`${API_BASE_URL}/api/auth/logout`);
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local state regardless of API call result
      localStorage.removeItem('authToken');
      setToken(null);
      setUser(null);
      setOrganization(null);
      setPermissions([]);
      delete axios.defaults.headers.common.Authorization;
    }
  }, []);

  // Load user data on mount if token exists
  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
          if (response.data.success) {
            setUser(response.data.data.user);
            setOrganization(response.data.data.organization);
            setPermissions(response.data.data.permissions);
          } else {
            // Invalid token, clear it
            logout();
          }
        } catch (err: any) {
          console.error('Failed to load user data:', err);
          // If token is expired or invalid, logout
          if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            logout();
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [logout, token]);

  const login = useCallback(
    async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
      try {
        setError(null);
        const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
          username,
          password,
        });

        if (response.data.success) {
          const { token: authToken, user: userData, organization: orgData, permissions: perms } = response.data.data;

          // Store token
          localStorage.setItem('authToken', authToken);
          setToken(authToken);
          setUser(userData);
          setOrganization(orgData);
          setPermissions(perms);

          return { success: true };
        } else {
          setError(response.data.error || 'Login failed');
          return { success: false, error: response.data.error };
        }
      } catch (err: any) {
        const errorMessage: string = err.response?.data?.message || err.response?.data?.error || 'Login failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [],
  );

  const refreshToken = useCallback(async (): Promise<{ success: boolean }> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`);
      if (response.data.success) {
        const newToken: string = response.data.data.token;
        localStorage.setItem('authToken', newToken);
        setToken(newToken);
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('Token refresh failed:', err);
      logout();
      return { success: false };
    }
  }, [logout]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!permissions || permissions.length === 0) {
        return false;
      }

      // Super admin has wildcard permission
      if (permissions.includes('*')) {
        return true;
      }

      return permissions.includes(permission);
    },
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (permissionList: string[]): boolean => {
      if (!permissions || permissions.length === 0) {
        return false;
      }

      // Super admin has wildcard permission
      if (permissions.includes('*')) {
        return true;
      }

      return permissionList.some((perm) => permissions.includes(perm));
    },
    [permissions],
  );

  const hasAllPermissions = useCallback(
    (permissionList: string[]): boolean => {
      if (!permissions || permissions.length === 0) {
        return false;
      }

      // Super admin has wildcard permission
      if (permissions.includes('*')) {
        return true;
      }

      return permissionList.every((perm) => permissions.includes(perm));
    },
    [permissions],
  );

  const isSuperAdmin = useCallback((): boolean => {
    return permissions.includes('*');
  }, [permissions]);

  const isTenantAdmin = useCallback((): boolean => {
    return permissions.includes('*') || permissions.includes('users.create');
  }, [permissions]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      organization,
      permissions,
      token,
      loading,
      error,
      isAuthenticated: !!user && !!token,
      login,
      logout,
      refreshToken,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      isSuperAdmin,
      isTenantAdmin,
    }),
    [
      user,
      organization,
      permissions,
      token,
      loading,
      error,
      login,
      logout,
      refreshToken,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      isSuperAdmin,
      isTenantAdmin,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
